import { MockAnalyticsAdapter } from "./mock-adapter";
import { PostHogAdapter } from "./posthog-adapter";
import type { AnalyticsAdapter } from "./types";

/**
 * Analytics service singleton.
 *
 * Provides a global interface for capturing analytics events.
 * Supports multiple adapters that receive all events (composite pattern).
 *
 * Usage:
 *   // Initialize (typically done once at SDK init)
 *   AnalyticsService.init("http://localhost:9000");
 *
 *   // Capture events - broadcasts to all registered adapters
 *   AnalyticsService.capture("checkout_page_ready", { session_id: "..." });
 *
 *   // For testing - add a mock adapter
 *   import { MockAnalyticsAdapter } from "./mock-adapter";
 *   const mock = new MockAnalyticsAdapter();
 *   AnalyticsService.addAdapter(mock);
 *
 *   // Or use the built-in test mock (exposed globally as window.__paymentKitAnalytics__)
 *   AnalyticsService.enableTestMode();
 *   // Events can be read via window.__paymentKitAnalytics__.getEvents()
 */
class AnalyticsServiceClass {
  private adapters: AnalyticsAdapter[] = [];
  private testMock: MockAnalyticsAdapter | null = null;

  /**
   * Initialize the analytics service with PostHog.
   *
   * @param apiBaseUrl - The API base URL for the reverse proxy
   * @param enableTestMode - Enable test mode for capturing events in tests
   */
  init(apiBaseUrl: string, enableTestMode?: boolean): void {
    try {
      // Add PostHog adapter (singleton - safe to call multiple times with same URL)
      this.addAdapter(PostHogAdapter.getInstance(apiBaseUrl));

      // Enable test mode if explicitly requested
      if (enableTestMode) {
        this.enableTestMode();
      }
    } catch (e) {
      // Silent fail - analytics should never break checkout
      console.warn("[PaymentKit Analytics] Init failed:", e);
    }
  }

  /**
   * Enable test mode by adding a mock adapter and exposing it globally.
   * The mock adapter is accessible via window.__paymentKitAnalytics__
   */
  enableTestMode(): void {
    if (this.testMock) return; // Already enabled

    this.testMock = new MockAnalyticsAdapter();
    this.addAdapter(this.testMock);

    // Expose globally for Playwright/test access
    if (typeof window !== "undefined") {
      window.__paymentKitAnalytics__ = this.testMock;
    }
  }

  /**
   * Get the test mock adapter (if test mode is enabled).
   */
  getTestMock(): MockAnalyticsAdapter | null {
    return this.testMock;
  }

  /**
   * Add an analytics adapter.
   * Events will be broadcast to all registered adapters.
   * Duplicate adapters (same instance) are ignored.
   *
   * @param adapter - The adapter to add
   */
  addAdapter(adapter: AnalyticsAdapter): void {
    if (!this.adapters.includes(adapter)) {
      this.adapters.push(adapter);
    }
  }

  /**
   * Clear all adapters.
   */
  clearAdapters(): void {
    this.adapters = [];
    this.testMock = null;
    if (typeof window !== "undefined") {
      delete window.__paymentKitAnalytics__;
    }
  }

  /**
   * Set the distinct_id for all adapters.
   * This is used to correlate frontend events with backend events.
   * Should be called with the checkout session secure token.
   */
  identify(distinctId: string): void {
    for (const adapter of this.adapters) {
      try {
        adapter.identify(distinctId);
      } catch {
        // Silent fail - never break checkout
      }
    }
  }

  /**
   * Capture an analytics event.
   * Broadcasts to all registered adapters. Silently fails on errors.
   */
  capture(event: string, properties?: Record<string, unknown>): void {
    for (const adapter of this.adapters) {
      try {
        adapter.capture(event, properties);
      } catch {
        // Silent fail - never break checkout
      }
    }
  }

  /**
   * Check if any adapter is initialized.
   */
  isInitialized(): boolean {
    return this.adapters.some((adapter) => adapter.isInitialized());
  }
}

/**
 * Global analytics service instance.
 */
export const AnalyticsService = new AnalyticsServiceClass();
